import { useState } from 'react';
import { apiClient } from '@/services/apiClient';
import { validateAssetBatch, normalizeAssetBatch } from '../utils/assetValidation';

interface UploadProgress {
  total: number;
  completed: number;
  failed: number;
  isUploading: boolean;
}

export interface AssetUpload {
  id: string;
  title: string;
  description?: string;
  source?: 'Impact' | 'Brandfolder' | 'Local';
  type?: 'Image' | 'Video';
  village_id?: string;
  program?: string;
  asset_type?: string;
  event?: string;
  date?: string;
  /**
   * For `Local` source uploads only. Returned by the staging upload
   * (`POST /impact/staging-upload`) and consumed by `impact/upload` so
   * Brandfolder can fetch the file from its short-lived storage URL.
   * See docs/asset-management-upload-photo-sources.md §2.
   */
  fileUrl?: string;
  /**
   * For `Local` source uploads only. Canonical RGF filename built on the
   * client per the docs §Filename Conventions; becomes the final
   * Brandfolder asset name. Whatever the client sends here is final —
   * the server does not validate against the staging response.
   */
  filename?: string;
}

interface UploadResult {
  success: Array<{
    id: string;
    bf_asset_id: string;
  }>;
  failed: Array<{
    id: string;
    error: string;
  }>;
}

interface UploadResponse {
  message: string;
  data: UploadResult;
}

export const usePhotoUpload = () => {
  const [progress, setProgress] = useState<UploadProgress>({
    total: 0,
    completed: 0,
    failed: 0,
    isUploading: false,
  });
  const [error, setError] = useState<string | null>(null);

  const programDisplayName: Record<string, string> = {
    'SD': 'Sports-Center',
    'TBTP': 'TBTP',
    'WM': 'WM',
    'RF': 'Regen-Agriculture',
    'LL': 'Learn-Lab',
    'YOGA': 'Yoga',
  };

  const generateTitle = (_photoId: string, photoData?: any): string => {
    // Format: RGF-Program-VillageName-District
    if (photoData) {
      const village = (photoData.village_name || '').trim().replace(/\s+/g, '-');
      const district = (photoData.district_name || '').trim().replace(/\s+/g, '-');
      const programKey = (photoData.program || '').trim();
      const program = programDisplayName[programKey] || programKey.replace(/\s+/g, '-');

      const titleParts = ['RGF', program, village, district].filter(part => part);
      return titleParts.join('-');
    }

    return 'RGF-Impact-Photo-Asset';
  };

  const generateDescription = (_photoId: string, _photoData?: any): string => {
    // Empty by default; optional description comes from user/API via photoData elsewhere.
    return '';
  };

  const detectAssetType = (photoId: string, mimeType?: string): 'Image' | 'Video' => {
    // Auto-detect based on mime type if available
    if (mimeType) {
      if (mimeType.startsWith('video/')) return 'Video';
      if (mimeType.startsWith('image/')) return 'Image';
    }

    // Fallback to filename extension detection
    const extension = photoId.split('.').pop()?.toLowerCase();
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];

    if (extension && videoExtensions.includes(extension)) {
      return 'Video';
    }

    return 'Image'; // Default to Image
  };

  const uploadPhotos = async (
    photoIds: string[],
    customAssets?: AssetUpload[]
  ): Promise<UploadResult | null> => {
    if (photoIds.length === 0 && (!customAssets || customAssets.length === 0)) {
      setError('No photos selected for upload');
      return null;
    }

    // Create asset objects with proper structure
    const rawAssets: AssetUpload[] = customAssets || photoIds.map(id => ({
      id,
      title: generateTitle(id),
      description: generateDescription(id),
      source: 'Impact',
      type: detectAssetType(id)
    }));

    // Validate assets before upload
    const validationResult = validateAssetBatch(rawAssets);
    if (!validationResult.isValid) {
      setError(`Validation failed: ${validationResult.errors.join('; ')}`);
      setProgress({
        total: rawAssets.length,
        completed: 0,
        failed: rawAssets.length,
        isUploading: false,
      });
      return null;
    }

    // Normalize assets
    const assets = normalizeAssetBatch(rawAssets);

    setProgress({
      total: assets.length,
      completed: 0,
      failed: 0,
      isUploading: true,
    });
    setError(null);

    try {
      const response = await apiClient.post<UploadResponse>(
        '/console/asset_management/impact/upload',
        {
          assets,
        }
      );

      const result = response.data.data;

      setProgress({
        total: assets.length,
        completed: result.success.length,
        failed: result.failed.length,
        isUploading: false,
      });

      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Upload failed';
      setError(errorMessage);

      setProgress({
        total: assets.length,
        completed: 0,
        failed: assets.length,
        isUploading: false,
      });

      return null;
    }
  };

  const resetProgress = () => {
    setProgress({
      total: 0,
      completed: 0,
      failed: 0,
      isUploading: false,
    });
    setError(null);
  };

  return {
    uploadPhotos,
    progress,
    error,
    resetProgress,
    generateTitle,
    generateDescription,
    detectAssetType,
  };
};